import { CourseImageConstants } from '../constants/CourseImageConstants';

// How a closed course is shown, decided in one place so every surface agrees.
//
// A course marked Closed (Course.Closed) shows the shared "Closed" logo in place of its own,
// whether or not it has one, and says when it closed. Its own logo is never removed: the API still
// sends it, and the admin edit page (CourseForm) still shows it, so unticking Closed brings it back.
//
// When it closed is a year and an optional month, because the legacy data only ever recorded a
// year ("Bull Run Country Club (CLOSED 2005)"). So the label reads "Closed December 2025" when an
// admin has set the month, "Closed 2005" when only the year is known, and plain "Closed" otherwise.

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const monthName = (month: number): string | null =>
  Number.isInteger(month) && month >= 1 && month <= 12
    ? MONTHS[month - 1]
    : null;

export interface CourseClosure {
  closed?: boolean;
  closedYear?: number | null;
  closedMonth?: number | null;
}

// "Closed December 2025", "Closed 2005", "Closed", or null for an open course.
export const closedLabel = ({
  closed,
  closedYear,
  closedMonth,
}: CourseClosure): string | null => {
  if (!closed) {
    return null;
  }
  if (closedYear == null) {
    return 'Closed';
  }
  const month = closedMonth != null ? monthName(closedMonth) : null;
  return month != null
    ? `Closed ${month} ${closedYear}`
    : `Closed ${closedYear}`;
};

interface CourseLogoInput {
  closed?: boolean;
  logoUrl?: string | null;
}

// The logo to draw for a course. `hasOwnLogo` is false for both stand-ins (the GolfPlayed mark and
// the Closed artwork): they are dark tiles, and the surfaces that put a real logo on a white plate
// must not put these on one.
export const courseLogo = ({
  closed,
  logoUrl,
}: CourseLogoInput): { src: string; hasOwnLogo: boolean } => {
  if (closed) {
    return { src: CourseImageConstants.CLOSED_LOGO, hasOwnLogo: false };
  }
  if (logoUrl != null && logoUrl.length > 0) {
    return { src: logoUrl, hasOwnLogo: true };
  }
  return { src: CourseImageConstants.DEFAULT_LOGO, hasOwnLogo: false };
};
